import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { utils, site, siteLocales, i18next, integration } from '@yola/ws-sdk';
import { reactHookForm } from '@yola/ws-ui';
import websiteSettings from 'src/js/modules/website-settings';
import dialogTypes from '../../dialogs/constants/dialog-types';
import TranslatePageDialog from '../components/translate-page-dialog';
import fieldNames from '../constants/field-names';
import translateExistingPageOptionsValues from '../constants/translate-existing-page-options-values';

const { useForm } = reactHookForm;

const { helpers } = websiteSettings;

const requireValidation = (value) => {
  if (!value) {
    return i18next.t('This field is required');
  }
  return true;
};

const getCaptions = ({ pages, languages, pageId, langCode }) => {
  const pageName = pages.find((page) => page.id === pageId).name;
  const language = languages.find((option) => option.code === langCode).name;

  return {
    title: i18next.t('Translate page'),
    fieldLabels: {
      page: i18next.t('Select a page'),
      language: i18next.t('Translate to'),
      checkbox: i18next.t('Translate with'),
    },
    selectPlaceholder: i18next.t('Select...'),
    noResultsText: i18next.t('No results found'),
    submit: i18next.t('Submit'),
    cancel: i18next.t('Cancel'),
    radioButtonGroupCaptions: {
      title: `${i18next.t('Override existing page')}?`,
      description: i18next.t(
        'You already have a "{pageName}" page in {language}. Override it or create a copy?',
        {
          pageName,
          language,
        }
      ),
      radioButton1: i18next.t('Override "{pageName}"', { pageName }),
      radioButton2: i18next.t('Make a copy'),
    },
  };
};

function TranslatePageDialogContainer({
  onSubmit = utils.noop,
  onCancel = utils.noop,
  captions: propsCaptions = {},
  initialValues = null,
  currentPageId,
  onDialogMainAction,
  onDialogCancel,
}) {
  const countries = useMemo(() => helpers.getCountries(), []);
  const languages = useMemo(() => helpers.getLanguages(), []);
  const pages = useSelector(site.selectors.getPages);
  const currentLocale = useSelector(siteLocales.selectors.getCurrentLocale);
  const usedLocales = useSelector(siteLocales.selectors.getSiteLocales);
  const filteredUsedLocales = usedLocales.filter((code) => code !== currentLocale);
  const supportedLocales = useSelector(siteLocales.selectors.getSupportedLocales);
  const requestBlockData = useSelector(integration.selectors.getRequestBlock);
  const roadmapUrl = requestBlockData && requestBlockData.publicRoadmapUrl;

  const usedLanguageOptions = useMemo(
    () =>
      helpers.getRelatedLanguageOptions(
        supportedLocales.filter((code) => !filteredUsedLocales.includes(code)),
        languages,
        countries
      ),
    [languages, countries, filteredUsedLocales, supportedLocales]
  );

  const allLanguageOptions = useMemo(
    () => helpers.getRelatedLanguageOptions(usedLocales, languages, countries),
    [languages, countries, usedLocales]
  );

  const groupLanguageOptions = [
    {
      value: 'all-languages',
      label: i18next.t('All languages'),
      options: allLanguageOptions,
    },
  ];

  if (usedLanguageOptions.length) {
    groupLanguageOptions.unshift({
      value: 'used-languages',
      label: i18next.t('Added languages'),
      options: usedLanguageOptions,
    });
  }

  const setInitialValue = (values) => {
    if (values) return values;

    const firstLangCode = usedLanguageOptions.length
      ? usedLanguageOptions[0].code
      : allLanguageOptions[0].code;

    return {
      langCode: firstLangCode,
      pageId: currentPageId,
      autoTrans: false,
      translateExistingPage: translateExistingPageOptionsValues.OVERRIDE_PAGE,
    };
  };

  const {
    control,
    handleSubmit: manageSubmit,
    watch,
  } = useForm({
    defaultValues: setInitialValue(initialValues),
  });
  const selectedPageId = watch(fieldNames.TRANSLATE_PAGE_ID);
  const selectedLangCode = watch(fieldNames.TRANSLATE_LANGUAGE_SELECT);

  const pagesOptions = useMemo(
    () =>
      pages.filter((page) => page.locale === currentLocale).map(({ name, id }) => ({ name, id })),
    [pages, currentLocale]
  );

  const handleSubmit = (...values) => {
    const [submitData] = values;
    const { pageId, langCode, autoTrans, translateExistingPage } = submitData;
    const targetPage = pages.find((page) => page.id === pageId);

    onDialogMainAction({
      dialogType: dialogTypes.TRANSLATE_PAGE,
      pageName: targetPage.name,
      pageLocale: currentLocale,
      targetLocale: langCode,
      aiTranslation: autoTrans,
      overridePage: translateExistingPage === translateExistingPageOptionsValues.OVERRIDE_PAGE,
    });

    onSubmit(...values);
  };

  const handleCancel = () => {
    onDialogCancel({ dialogType: dialogTypes.TRANSLATE_PAGE });
    onCancel();
  };

  const showRadioButtonGroup = useCallback(() => {
    const targetPage = pages.find((page) => page.id === selectedPageId);
    const targetLocalePages = pages.filter((page) => page.locale === selectedLangCode);

    return targetLocalePages.some((page) => page.name === targetPage.name);
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [selectedPageId, selectedLangCode]);

  const captions = useMemo(
    () => ({
      ...getCaptions({
        pages,
        languages: [...usedLanguageOptions, ...allLanguageOptions],
        pageId: selectedPageId,
        langCode: selectedLangCode,
      }),
      ...propsCaptions,
    }),
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
    [selectedPageId, selectedLangCode]
  );

  return (
    <TranslatePageDialog
      onSubmit={manageSubmit(handleSubmit)}
      onCancel={handleCancel}
      roadmapUrl={roadmapUrl}
      control={control}
      pagesOptions={pagesOptions}
      languageOptions={groupLanguageOptions}
      fieldsValidation={{
        page: requireValidation,
        langCode: requireValidation,
      }}
      captions={captions}
      showRadioButtonGroup={showRadioButtonGroup()}
      selectedPageId={selectedPageId}
    />
  );
}

TranslatePageDialogContainer.propTypes = {
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  captions: PropTypes.shape({}),
  initialValues: PropTypes.shape({
    pageId: PropTypes.string,
    langCode: PropTypes.string,
    autoTrans: PropTypes.bool,
  }),
  currentPageId: PropTypes.string.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default TranslatePageDialogContainer;
